import React, { useEffect, useRef, useState } from 'react'
import { DateInput } from '../../../../common/components/DateInput'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { UnsavedChangesModal } from '../../../../common/components/UnsavedChangesModal'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import { useUnsavedChanges } from '../../../../common/hooks/useUnsavedChanges'
import { customSwal } from '../../../../config/swal'
import {
  FundDraftService,
  FundSeriesTab,
  IPerformanceRecord,
} from '../../../../services/FundDraftService'
import { useFundSeries } from '../hooks/useFundSeries'

interface ILocalRecord {
  id: string | null
  date: string
  returns: Record<string, string>
  status: 'draft' | 'published'
  isDirty: boolean
}

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

const DEFAULT_KEYS = ['3M', '6M', '9M', '2023', '2024', '2025', 'YTD', 'TODOS']

const keyOrder = (key: string): [number, number] => {
  const monthMatch = key.match(/^(\d+)M$/)
  if (monthMatch) return [0, parseInt(monthMatch[1], 10)]
  const yearMatch = key.match(/^(\d{4})$/)
  if (yearMatch) return [1, parseInt(yearMatch[1], 10)]
  if (key === 'YTD') return [2, 0]
  if (key === 'TODOS') return [3, 0]
  return [4, 0]
}

const sortKeys = (keys: string[]): string[] =>
  [...keys].sort((a, b) => {
    const [ga, na] = keyOrder(a)
    const [gb, nb] = keyOrder(b)
    return ga !== gb ? ga - gb : na - nb
  })

const keysFromRaw = (records: IPerformanceRecord[]): string[] => {
  const seen: string[] = []
  const seenSet = new Set<string>()
  records.forEach((r) =>
    r.returns.forEach(({ period }) => {
      const normalized = period.toUpperCase()
      if (!seenSet.has(normalized)) {
        seen.push(normalized)
        seenSet.add(normalized)
      }
    })
  )
  return seen
}

const toDisplayDate = (date: string): string => {
  if (!date) return ''
  const parts = date.split('-')
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`
  return date
}

const toApiDate = (date: string): string => {
  if (!date) return ''
  const parts = date.split('/')
  if (parts.length === 3 && parts[2].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`
  return date
}

const toLocalRecord = (r: IPerformanceRecord): ILocalRecord => ({
  id: r.id,
  date: toDisplayDate(r.date),
  returns: Object.fromEntries(
    r.returns.map(({ period, value }) => [period.toUpperCase(), String(value)])
  ),
  status: r.status,
  isDirty: false,
})

const cellBox: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#fff',
  padding: '6px 8px',
  fontSize: 13,
  minWidth: 64,
  textAlign: 'center',
}

const thBox: React.CSSProperties = {
  ...cellBox,
  background: '#fafafa',
  fontWeight: 600,
  color: '#374151',
}

export const PerformanceTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const {
    data: seriesData,
    isLoading,
    performanceRecords: storeRecords,
    refetchPerformanceForSerie,
    refetchSeriesOnly,
  } = useFundSeries(fundId)

  const [activeSerieCode, setActiveSerieCode] = useState('')
  const [performanceAction, setPerformanceAction] = useState<'saving' | 'publishing' | null>(null)
  const [visibility, setVisibility] = useState(true)
  const [title, setTitle] = useState('')

  const [metaDirty, setMetaDirty] = useState(false)

  const [publishedRecords, setPublishedRecords] = useState<ILocalRecord[]>([])
  const [draftRecords, setDraftRecords] = useState<ILocalRecord[]>([])
  const [publishedKeys, setPublishedKeys] = useState<string[]>([])
  const [draftKeys, setDraftKeys] = useState<string[]>([...DEFAULT_KEYS])
  const [draftKeysSnapshot, setDraftKeysSnapshot] = useState<string[]>([])
  const [colEditMode, setColEditMode] = useState(false)

  const initialized = useRef(false)

  useEffect(() => {
    if (!activeSerieCode) return
    const incoming = storeRecords[activeSerieCode]
    if (!incoming) return
    const pubRaw = incoming.filter((r) => r.status === 'published')
    const draftRaw = incoming.filter((r) => r.status === 'draft')
    setPublishedRecords(pubRaw.map(toLocalRecord))
    setDraftRecords(draftRaw.map(toLocalRecord))
    setPublishedKeys(pubRaw.length > 0 ? sortKeys(keysFromRaw(pubRaw)) : [])
    const defaultDraftKeys =
      pubRaw.length > 0 ? sortKeys(keysFromRaw([pubRaw[0]])) : [...DEFAULT_KEYS]
    setDraftKeys(draftRaw.length > 0 ? sortKeys(keysFromRaw(draftRaw)) : defaultDraftKeys)
    setColEditMode(false)
    setMetaDirty(false)
  }, [storeRecords, activeSerieCode])

  const applySerieData = (code: string) => {
    if (!seriesData) return
    const perf = seriesData.tabs.performance[code]
    setVisibility(perf?.visibility ?? true)
    setTitle((perf as any)?.title ?? '')
  }

  useEffect(() => {
    if (!seriesData) return
    if (initialized.current) {
      if (activeSerieCode) applySerieData(activeSerieCode)
      return
    }
    const firstCode = seriesData.series[0]?.serie_codigo
    if (!firstCode) return
    initialized.current = true
    setActiveSerieCode(firstCode)
    applySerieData(firstCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData])

  const isDirty = metaDirty || draftRecords.some((r) => r.isDirty)

  // Column management (draft only)
  const enterColEditMode = () => {
    setDraftKeysSnapshot([...draftKeys])
    setColEditMode(true)
  }
  const confirmColEdit = () => {
    const validKeys = draftKeys.map((k) => k.trim().toUpperCase()).filter((k) => k !== '')
    if (new Set(validKeys).size !== validKeys.length) {
      customSwal.fire({ title: 'Hay columnas con nombre duplicado', icon: 'warning' })
      return
    }
    setDraftKeys(sortKeys(validKeys))
    setDraftKeysSnapshot([])
    setMetaDirty(true)
    setColEditMode(false)
  }
  const cancelColEdit = () => {
    setDraftKeys([...draftKeysSnapshot])
    setColEditMode(false)
  }
  const removeColumn = (idx: number) => setDraftKeys((prev) => prev.filter((_, i) => i !== idx))
  const renameColumn = (idx: number, val: string) =>
    setDraftKeys((prev) => prev.map((k, i) => (i === idx ? val : k)))

  const addColumn = () => setDraftKeys((prev) => [...prev, ''])

  const getTodayDisplay = (): string => {
    const today = new Date()
    const dd = String(today.getDate()).padStart(2, '0')
    const mm = String(today.getMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}/${today.getFullYear()}`
  }

  const isDateTaken = (date: string, excludeIdx: number): boolean =>
    draftRecords.some((r, i) => i !== excludeIdx && r.date === date)

  // Draft row mutations
  const addDraftRow = (afterIdx?: number) => {
    const emptyReturns: Record<string, string> = {}
    draftKeys.forEach((k) => (emptyReturns[k] = ''))
    const defaultDate = isDateTaken(getTodayDisplay(), -1) ? '' : getTodayDisplay()
    const newRow: ILocalRecord = {
      id: null,
      date: defaultDate,
      returns: emptyReturns,
      status: 'draft',
      isDirty: true,
    }
    setDraftRecords((prev) => {
      const next = [...prev]
      const insertAt = afterIdx !== undefined ? afterIdx + 1 : next.length
      next.splice(insertAt, 0, newRow)
      return next
    })
  }

  const deleteDraftRow = (idx: number) => {
    if (draftRecords[idx]?.id !== null) setMetaDirty(true)
    setDraftRecords((prev) => prev.filter((_, i) => i !== idx))
  }

  const updateDraftDate = (idx: number, value: string) => {
    if (value.length === 10 && isDateTaken(value, idx)) return
    setDraftRecords((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], date: value, isDirty: true }
      return next
    })
  }

  const updateDraftValue = (idx: number, key: string, value: string) =>
    setDraftRecords((prev) => {
      const next = [...prev]
      next[idx] = {
        ...next[idx],
        returns: { ...next[idx].returns, [key]: value },
        isDirty: true,
      }
      return next
    })

  // Save & publish
  const savePerformanceData = (svc: ReturnType<typeof FundDraftService>) =>
    Promise.all([
      svc.updateSeriesTab(fundId, activeSerieCode, FundSeriesTab.PERFORMANCE, {
        visibility,
        title,
      }),
      svc.savePerformanceRecord(fundId, activeSerieCode, {
        items: draftRecords.map((r) => ({
          date: toApiDate(r.date),
          returns: draftKeys
            .filter((k) => k.trim() !== '')
            .map((k) => ({
              period: k.trim().toUpperCase(),
              value: (r.returns[k] ?? '').trim() !== '' ? r.returns[k] : '0.00',
            })),
        })),
      }),
    ])

  const refetchAll = () => {
    Promise.all([refetchPerformanceForSerie(activeSerieCode), refetchSeriesOnly()])
  }

  const validateDraftDates = (): boolean => {
    const invalid = draftRecords.some((r) => toApiDate(r.date).length !== 10)
    if (invalid) {
      customSwal.fire({ title: 'Hay filas con fecha vacía o incompleta', icon: 'warning' })
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateDraftDates()) return
    setPerformanceAction('saving')
    try {
      await savePerformanceData(FundDraftService())
      setMetaDirty(false)
      refetchAll()
      customSwal.fire({ title: 'Rendimiento guardado correctamente', icon: 'success' })
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al guardar el rendimiento'
      customSwal.fire({ title: msg, icon: 'error' })
    }
    setPerformanceAction(null)
  }

  const handlePublish = async () => {
    if (!validateDraftDates()) return
    setPerformanceAction('publishing')
    try {
      const svc = FundDraftService()
      await savePerformanceData(svc)
      await Promise.all([
        svc.publishPerformanceRecords(fundId, activeSerieCode),
        svc.publishSeriesTab(fundId, activeSerieCode, FundSeriesTab.PERFORMANCE),
      ])
      setMetaDirty(false)
      refetchAll()
      customSwal.fire({ title: 'Rendimiento publicado correctamente', icon: 'success' })
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al publicar el rendimiento'
      customSwal.fire({ title: msg, icon: 'error' })
    }
    setPerformanceAction(null)
  }

  const { guardNavigation, isModalOpen, handleLeave, handleCancel, handleSaveAndContinue, saving } =
    useUnsavedChanges(isDirty, handleSave)

  const handleSerieChange = (code: string) => {
    guardNavigation(() => {
      setMetaDirty(false)
      setActiveSerieCode(code)
      applySerieData(code)
      setColEditMode(false)
    })
  }

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  if (isLoading && !seriesData) return <TabSkeleton />

  const edicionActivoBadge = seriesData ? (
    <span
      style={{
        background: '#d4edda',
        color: '#155724',
        fontSize: '12px',
        padding: '2px 10px',
        borderRadius: 20,
        fontWeight: 600,
      }}
    >
      Edición activo
    </span>
  ) : null

  const seriesTabs =
    seriesData && seriesData.series.length > 0 ? (
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #f1f3f5',
          marginTop: '12px',
          marginLeft: '-20px',
          marginRight: '-20px',
          paddingLeft: '20px',
        }}
      >
        {seriesData.series.map((serie) => (
          <button
            key={serie.serie_codigo}
            type='button'
            onClick={() => handleSerieChange(serie.serie_codigo)}
            style={{
              border: 'none',
              background: 'none',
              padding: '6px 20px',
              cursor: 'pointer',
              fontWeight: activeSerieCode === serie.serie_codigo ? 600 : 400,
              color:
                activeSerieCode === serie.serie_codigo ? 'var(--color-primary-blue)' : '#9ca3af',
              borderBottom:
                activeSerieCode === serie.serie_codigo
                  ? '2px solid var(--color-primary-blue)'
                  : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '14px',
              transition: 'color 0.15s',
            }}
          >
            {serie.serie_name}
          </button>
        ))}
      </div>
    ) : null

  return (
    <div>
      {/* Header */}
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center gap-3 mt-2 mb-1'>
          <span className='text-gray-800 fs-2 fw-bold'>Resultados de fondo mutuo</span>
          {edicionActivoBadge}
        </div>
        {seriesTabs}
      </div>

      {/* Visibilidad + Título */}
      <div className='card mt-5 p-5'>
        <div className='d-flex align-items-center gap-3 mb-5'>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={visibility}
              onChange={(e) => {
                setVisibility(e.target.checked)
                setMetaDirty(true)
              }}
            />
          </div>
          <label className='fw-semibold fs-6 text-gray-700 mb-0'>Mostrar información</label>
        </div>
        <label className='fw-bold fs-5 text-gray-800 mb-2'>Título</label>
        <input
          type='text'
          className='form-control'
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            setMetaDirty(true)
          }}
          placeholder='Ingresar título'
        />
      </div>

      {/* Publicados */}
      {publishedRecords.length > 0 && (
        <div className='card mt-5 p-5' style={{ overflowX: 'auto' }}>
          <span className='fw-bold fs-6 text-gray-600 d-block mb-4'>Publicados</span>
          <table className='w-100' style={{ borderCollapse: 'separate', borderSpacing: '4px 4px' }}>
            <thead>
              <tr>
                <th style={{ ...thBox, width: 160, textAlign: 'left', minWidth: 160 }}>Fecha</th>
                {publishedKeys.map((key) => (
                  <th key={key} style={thBox}>
                    {key}
                  </th>
                ))}
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {publishedRecords.map((row, idx) => (
                <tr key={idx}>
                  <td>
                    <div
                      style={{
                        ...cellBox,
                        background: '#f9fafb',
                        color: '#6b7280',
                        textAlign: 'left',
                      }}
                    >
                      {row.date}
                    </div>
                  </td>
                  {publishedKeys.map((key) => {
                    const hasValue = row.returns[key] !== undefined && row.returns[key] !== ''
                    return (
                      <td key={key}>
                        {hasValue ? (
                          <div style={{ ...cellBox, background: '#f9fafb', color: '#6b7280' }}>
                            {row.returns[key]}
                          </div>
                        ) : (
                          <div
                            style={{
                              ...cellBox,
                              background: '#fff5f5',
                              border: '1px solid #ff0000',
                              color: '#b91c1c',
                              fontSize: 11,
                            }}
                          >
                            Sin valor
                          </div>
                        )}
                      </td>
                    )
                  })}
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Borrador */}
      <div className='card mt-5 p-5' style={{ overflowX: 'auto' }}>
        <span className='fw-bold fs-6 text-gray-600 d-block mb-4'>Borrador</span>
        <table className='w-100' style={{ borderCollapse: 'separate', borderSpacing: '4px 4px' }}>
          <thead>
            <tr>
              <th style={{ ...thBox, width: 160, textAlign: 'left', minWidth: 160 }}>Fecha</th>
              {draftKeys.map((key, keyIdx) => (
                <th key={keyIdx} style={{ ...thBox, padding: colEditMode ? '4px' : undefined }}>
                  {colEditMode ? (
                    <div className='d-flex align-items-center gap-1'>
                      <input
                        type='text'
                        value={key}
                        onChange={(e) => renameColumn(keyIdx, e.target.value)}
                        style={{
                          width: 52,
                          fontSize: 12,
                          textAlign: 'center',
                          border: '1px solid #d1d5db',
                          borderRadius: 4,
                          padding: '2px 4px',
                        }}
                      />
                      <button
                        type='button'
                        onClick={() => removeColumn(keyIdx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          lineHeight: 1,
                        }}
                      >
                        <i className='bi bi-x' style={{ fontSize: '0.85rem', color: '#ef4444' }} />
                      </button>
                    </div>
                  ) : (
                    key
                  )}
                </th>
              ))}
              {/* Column toolbar */}
              <th style={{ width: 'auto', verticalAlign: 'middle', padding: '4px' }}>
                <div className='d-flex gap-1 align-items-center'>
                  {colEditMode ? (
                    <>
                      <button
                        type='button'
                        className='btn btn-sm btn-custom-action'
                        style={{ padding: '4px 8px' }}
                        onClick={confirmColEdit}
                        title='Confirmar'
                      >
                        <i className='bi bi-check-lg' style={{ fontSize: '0.75rem' }} />
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-custom-action'
                        style={{ padding: '4px 8px' }}
                        onClick={addColumn}
                        title='Agregar columna'
                      >
                        <i className='bi bi-plus-lg' style={{ fontSize: '0.75rem' }} />
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm btn-custom-action'
                        style={{ padding: '4px 8px' }}
                        onClick={cancelColEdit}
                        title='Cancelar y revertir'
                      >
                        <i className='bi bi-x-lg' style={{ fontSize: '0.75rem' }} />
                      </button>
                    </>
                  ) : (
                    <button
                      type='button'
                      className='btn btn-sm btn-custom-action'
                      style={{ padding: '4px 8px' }}
                      onClick={enterColEditMode}
                      disabled={!hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)}
                      title='Editar columnas'
                    >
                      <i className='bi bi-pencil' style={{ fontSize: '0.75rem' }} />
                    </button>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {draftRecords.length === 0 && (
              <tr>
                <td
                  colSpan={draftKeys.length + 2}
                  className='text-center text-muted py-4'
                  style={{ fontSize: 13 }}
                >
                  Sin borradores. Agrega una fila con el botón de abajo.
                </td>
              </tr>
            )}
            {draftRecords.map((row, idx) => (
              <tr key={idx}>
                <td>
                  <DateInput
                    className='form-control form-control-sm'
                    value={row.date}
                    onChange={(val) => updateDraftDate(idx, val)}
                    style={{ fontSize: 13, borderRadius: 8 }}
                    disabled={
                      colEditMode || !hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)
                    }
                  />
                </td>
                {draftKeys.map((key, keyIdx) => (
                  <td key={keyIdx}>
                    <input
                      type='number'
                      className='form-control form-control-sm text-center'
                      value={row.returns[key] ?? ''}
                      onChange={(e) => updateDraftValue(idx, key, e.target.value)}
                      step='0.01'
                      style={{ minWidth: 64, borderRadius: 8 }}
                      disabled={
                        colEditMode || !hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)
                      }
                    />
                  </td>
                ))}
                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                  <div className='d-flex gap-1 justify-content-center'>
                    <button
                      type='button'
                      className='btn btn-icon btn-sm'
                      style={{
                        border: '1px solid #e5e7eb',
                        background: 'transparent',
                        borderRadius: 6,
                      }}
                      onClick={() => addDraftRow(idx)}
                      disabled={
                        colEditMode || !hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)
                      }
                      title='Agregar fila debajo'
                    >
                      <i
                        className='bi bi-plus-lg'
                        style={{ fontSize: '0.75rem', color: '#6b7280' }}
                      />
                    </button>
                    <button
                      type='button'
                      className='btn btn-icon btn-sm'
                      style={{
                        border: '1px solid #e5e7eb',
                        background: 'transparent',
                        borderRadius: 6,
                      }}
                      onClick={() => deleteDraftRow(idx)}
                      disabled={
                        colEditMode || !hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)
                      }
                      title='Eliminar fila'
                    >
                      <i
                        className='bi bi-trash'
                        style={{ fontSize: '0.75rem', color: '#6b7280' }}
                      />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className='mt-3'>
          <button
            type='button'
            className='btn btn-sm btn-custom-action'
            onClick={() => addDraftRow()}
            disabled={colEditMode || !hasPermission(Permissions.FUND_TABS.PERFORMANCE.RECORD_EDIT)}
          >
            <i className='bi bi-plus-lg me-1' style={{ fontSize: '0.75rem' }} />
            Agregar fila
          </button>
        </div>
      </div>

      {/* Acciones */}
      {activeSerieCode &&
        hasPermission([
          Permissions.FUND_TABS.PERFORMANCE.SAVE,
          Permissions.FUND_TABS.PERFORMANCE.PUBLISH,
        ]) && (
          <div className='card mt-5 p-5'>
            <div className='d-flex justify-content-end gap-3'>
              {hasPermission(Permissions.FUND_TABS.PERFORMANCE.SAVE) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handleSave}
                  disabled={colEditMode || performanceAction !== null}
                >
                  {performanceAction === 'saving' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Guardando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              )}
              {hasPermission(Permissions.FUND_TABS.PERFORMANCE.PUBLISH) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handlePublish}
                  disabled={colEditMode || performanceAction !== null}
                >
                  {performanceAction === 'publishing' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Publicando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Publicar'
                  )}
                </button>
              )}
            </div>
          </div>
        )}

      {isModalOpen && (
        <UnsavedChangesModal
          onLeave={handleLeave}
          onCancel={handleCancel}
          onSave={handleSaveAndContinue}
          saving={saving}
        />
      )}
    </div>
  )
}
